import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appsApi, type App } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, FileText, Calculator, LayoutGrid } from 'lucide-react';
import { ViewManifestDialog } from '@/components/view-manifest-dialog';
import { QueryState } from '@/components/query-state';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'file-text': FileText,
  'calculator': Calculator,
  default: LayoutGrid,
};

function AppsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {[1, 2].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader>
            <div className="h-6 w-32 bg-muted rounded" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="h-4 w-48 bg-muted rounded" />
              <div className="h-4 w-32 bg-muted rounded" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function AppsPage() {
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  const apps = data?.data || [];

  return (
    <div className="space-y-6">
      <div>
        <span className="rf3-eyebrow">Administration</span>
        <h2 className="text-3xl font-bold tracking-tight text-primary">App registry</h2>
        <p className="text-muted-foreground">
          View and manage registered applications
        </p>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={apps.length === 0}
        skeleton={<AppsSkeleton />}
        emptyMessage="No apps registered"
      >
        <div className="grid gap-4 md:grid-cols-2">
          {apps.map((app) => {
            const manifest = app.manifest as {
              ui?: { baseUrl?: string; nav?: { icon?: string; category?: string } };
              auth?: { publicRoutes?: string[] };
              permissions?: { requiredAnyOf?: string[]; requiredAllOf?: string[] };
            };
            const Icon = iconMap[manifest.ui?.nav?.icon || 'default'] || iconMap.default;

            return (
              <Card key={app.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{app.name}</CardTitle>
                        <CardDescription>{manifest.ui?.nav?.category}</CardDescription>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        app.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : app.status === 'maintenance'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {app.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">{app.description}</p>

                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Base URL: </span>
                      <a
                        href={manifest.ui?.baseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        {manifest.ui?.baseUrl}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>

                    {manifest.auth?.publicRoutes && manifest.auth.publicRoutes.length > 0 && (
                      <div>
                        <span className="font-medium">Public Routes: </span>
                        <span className="text-muted-foreground">
                          {manifest.auth.publicRoutes.join(', ')}
                        </span>
                      </div>
                    )}

                    {manifest.permissions?.requiredAnyOf && (
                      <div>
                        <span className="font-medium">Required Permissions: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {manifest.permissions.requiredAnyOf.map((p) => (
                            <span key={p} className="px-2 py-0.5 bg-muted text-xs rounded">
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedApp(app)}
                    >
                      View Manifest
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </QueryState>

      <ViewManifestDialog
        appName={selectedApp?.name || ''}
        manifest={selectedApp?.manifest ?? {}}
        open={!!selectedApp}
        onOpenChange={(open) => {
          if (!open) setSelectedApp(null);
        }}
      />
    </div>
  );
}
